/**
 * Typography Demo Page
 * Showcases the design system typography integration
 */

import React from 'react';
import { 
  Typography, 
  Heading1, 
  Heading2, 
  Heading3, 
  Heading4, 
  BodyText, 
  Caption, 
  Label 
} from '../components/ui/Typography';
import { PageContainer } from '../components/PageContainer';

export const TypographyDemo: React.FC = () => {
  return (
    <PageContainer variant="white" className="space-y-8">
      <div>
        <Heading1>Design System Typography</Heading1>
        <BodyText className="mt-2 text-gray-600">
          This page demonstrates the integrated design system typography components.
        </BodyText>
      </div>

      {/* General Typography */}
      <section className="space-y-4">
        <Heading2>General Typography</Heading2>
        
        <div className="space-y-3">
          <div>
            <Label>Heading 1 (Serif, 400)</Label>
            <Heading1>The Art of Wine Making</Heading1>
          </div>

          <div>
            <Label>Heading 2 (Serif, 400)</Label>
            <Heading2>Exploring Napa Valley</Heading2>
          </div>

          <div>
            <Label>Heading 3 (Sans, Semibold)</Label>
            <Heading3>Tasting Notes & Reviews</Heading3>
          </div>

          <div>
            <Label>Heading 4 (Sans, Semibold)</Label>
            <Heading4>Featured Wineries</Heading4>
          </div>

          <div>
            <Label>Body Text (Sans, Normal)</Label>
            <BodyText>
              Wine tasting is an art form that combines sensory experience with cultural appreciation.
              Each bottle tells a story of terroir, craftsmanship, and tradition that spans generations
              of winemaking expertise.
            </BodyText>
          </div>

          <div>
            <Label>Caption (Sans, Small)</Label>
            <Caption>Photo courtesy of Napa Valley Wine Association</Caption>
          </div>
        </div>
      </section>

      {/* Wine-Specific Typography */}
      <section className="space-y-4">
        <Heading2>Wine-Specific Typography</Heading2>
        
        <div className="bg-gray-50 p-6 rounded-lg space-y-4">
          <div>
            <Label>Wine Name</Label>
            <Typography variant="wineName">Château Margaux 2015</Typography>
          </div>
          
          <div>
            <Label>Wine Varietal</Label>
            <Typography variant="wineVarietal">Cabernet Sauvignon Blend</Typography>
          </div>
          
          <div>
            <Label>Wine Vintage</Label>
            <Typography variant="wineVintage">2015</Typography>
          </div>
        </div>
      </section>

      {/* Venue-Specific Typography */}
      <section className="space-y-4">
        <Heading2>Venue-Specific Typography</Heading2>
        
        <div className="bg-gray-50 p-6 rounded-lg space-y-4">
          <div>
            <Label>Venue Name</Label>
            <Typography variant="venueName">Opus One Winery</Typography>
          </div>
          
          <div>
            <Label>Venue Location</Label>
            <Typography variant="venueLocation">Oakville, Napa Valley, California</Typography>
          </div>
        </div>
      </section>

      {/* Review Typography */}
      <section className="space-y-4">
        <Heading2>Review Typography</Heading2>
        
        <div className="bg-gray-50 p-6 rounded-lg space-y-4">
          <div>
            <Label>Review Title</Label>
            <Typography variant="reviewTitle">Exceptional vintage with remarkable depth</Typography>
          </div>
          
          <div>
            <Label>Review Body</Label>
            <Typography variant="reviewBody">
              This wine showcases incredible complexity with notes of dark cherry, cassis, and subtle oak. 
              The tannins are well-integrated, providing structure without overwhelming the fruit. 
              A truly memorable experience that will age beautifully for decades to come.
            </Typography>
          </div>
          
          <div>
            <Label>Review Rating</Label>
            <Typography variant="reviewRating">95/100 POINTS</Typography>
          </div>
        </div>
      </section>

      {/* Font Families Demo */}
      <section className="space-y-4">
        <Heading2>Font Families</Heading2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-100 p-4 rounded-lg">
            <Label>Sans (Geist)</Label>
            <p className="font-sans text-lg mt-2">
              UI chrome, buttons, labels, admin surfaces, and long-form body.
            </p>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg">
            <Label>Serif (Young Serif)</Label>
            <p className="font-serif text-lg mt-2">
              Editorial display — wine names, venue names, review titles.
            </p>
          </div>
        </div>
      </section>
    </PageContainer>
  );
};